<template>
  <div class="video-stage">
    <div class="relative flex h-[70lvh] min-h-min w-full flex-col overflow-hidden">
      <VideoComponent
        ref="player"
        :video="video"
        autoplay="true"
        :load="true"
        :show-video-in-loop="playbackMode === 'loop'"
        :image="posterImage"
        :controls="false"
        :muted="true"
        class="z-1 absolute h-full w-full object-cover"
      />

      <div
        class="video-stage-content container relative inset-0 z-10 mx-auto flex h-full min-h-min flex-col gap-6 py-24 md:py-32"
        :class="headlineAtBottom ? 'place-content-end' : 'place-content-center'"
      >
        <div
          v-intersect="{ class: ['!translate-x-0', 'opacity-100'], delay: 500 }"
          class="flex max-w-screen-sm content-end opacity-0 transition-all duration-500 motion-safe:translate-x-[50%] md:max-w-screen-md lg:max-w-screen-lg"
        >
          <Heading
            v-if="headline.text"
            color="light"
            :tag="headline?.tag ?? 'h1'"
            :size="headingSizeMapping[headline?.fontSize ?? 'xxxlarge']"
            class="h-full content-end md:h-auto"
            :class="
              contentToTailwindClasses({
                highlight: headline?.highlight,
                fontFamily: headline?.fontFamily,
              })
            "
          >
            {{ headline.text }}
          </Heading>
        </div>

        <h3
          v-typewriter="{ delay: 1000 }"
          style="opacity: 0"
          class="max-w-screen-sm content-end text-left text-2xl font-medium text-white md:max-w-screen-md lg:max-w-screen-lg xl:text-4xl"
        >
          {{ text }}
        </h3>
        <button
          class="container absolute bottom-0 left-0 m-4 flex h-16 w-16 overflow-hidden rounded-full bg-white p-0 md:h-24 md:w-24"
          @click="togglePlay"
        >
          <div v-if="isPlaying" class="mx-auto flex gap-1.5 self-center">
            <div class="h-6 w-1.5 bg-black xl:h-8 xl:w-2" />
            <div class="h-6 w-1.5 bg-black xl:h-8 xl:w-2" />
          </div>
          <VideoPlayButton v-else />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Heading, type HeadingProps } from "~/components/clarity-ui/Heading";
import VideoComponent from "~/templates/components/cms/VideoComponent.vue";
import { contentToTailwindClasses } from "~/utils/helper/tailwind";
import VideoPlayButton from "~/templates/elements/VideoPlayButton.vue";

const headingSizeMapping = ["xxlarge", "xlarge", "large", "medium", "xxxlarge"] as Pick<
  HeadingProps,
  "size"
>;
defineProps(["video", "headline", "text", "posterImage", "playbackMode", "headlineAtBottom"]);

const player = ref(null);
const isPlaying = ref(true);

const togglePlay = () => {
  const video = player.value;
  if (!video) return;

  if (video.media?.paused) {
    video.playVideo();
    isPlaying.value = true;
  } else {
    video.pauseVideo();
    isPlaying.value = false;
  }
};
</script>

<style scoped>
.video-stage {
  .video-stage-content {
    margin-top: var(--fixed-header-height);
  }
}
</style>
